import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, RotateCcw, Home } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { POLLING_CONFIG, STORAGE_KEYS } from '../config';
import type { WebinarResults } from '../types/api';
import { getScopedClient } from '../lib/supabase';
import { useTenant } from '../context/TenantContext';
import { AlienProcessor } from '../components/AlienProcessor';

const STAGE_MESSAGES = [
  'Analyzing your answers...',
  'Engineering your offer stack...',
  'Crafting your webinar script...',
  'Writing marketing emails...',
  'Generating social content...',
  'Finalizing your content...',
];

const STAGE_ROTATE_MS = 25000;

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

interface SupabaseWebinarRow {
  id: string;
  status: string;
  error_message: string | null;
  title: string | null;
  subtitle: string | null;
  webinar_script: string | null;
  marketing_emails: string | null;
  follow_up_emails: string | null;
  social_posts: string | null;
  landing_page: string | null;
  offer_stack: string | null;
}

type ProcessingState =
  | { kind: 'polling' }
  | { kind: 'complete'; results: WebinarResults }
  | { kind: 'error'; message: string; canRetry: boolean }
  | { kind: 'timeout' };

export function Processing() {
  const navigate = useNavigateWithParams();
  const { locationId } = useTenant();
  const [state, setState] = useState<ProcessingState>({ kind: 'polling' });
  const [elapsedMs, setElapsedMs] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);
  const [phase, setPhase] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stageRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(Date.now());

  const jobId = localStorage.getItem(STORAGE_KEYS.currentJobId);

  const cleanup = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (stageRef.current) clearInterval(stageRef.current);
  }, []);

  const handleCompleteFromResults = useCallback(
    (results: WebinarResults, webinarId?: string) => {
      // Store in localStorage as backup for direct navigation/refresh
      localStorage.setItem(STORAGE_KEYS.webinarResults, JSON.stringify(results));
      localStorage.removeItem(STORAGE_KEYS.currentJobId);

      // Navigate with optional webinar ID for Supabase-backed results
      const path = webinarId ? `/results/${webinarId}` : '/results';
      navigate(path, { state: { results }, replace: true });
    },
    [navigate],
  );

  const handleCompleteSupabase = useCallback(
    (row: SupabaseWebinarRow) => {
      if (!row.webinar_script || !row.marketing_emails || !row.social_posts || !row.follow_up_emails) {
        setState({
          kind: 'error',
          message: 'Results incomplete. Some content fields are missing. Please try again.',
          canRetry: true,
        });
        return;
      }

      const results: WebinarResults = {
        title: row.title || '',
        subtitle: row.subtitle || '',
        webinarScript: row.webinar_script,
        marketingEmails: row.marketing_emails,
        socialPosts: row.social_posts,
        followUpEmails: row.follow_up_emails,
        landingPage: row.landing_page || '',
        offerStack: row.offer_stack || '',
      };

      handleCompleteFromResults(results, row.id);
    },
    [handleCompleteFromResults],
  );

  const poll = useCallback(async () => {
    if (!jobId) return;

    try {
      // Poll Supabase (scoped client already configured by TenantProvider)
      const { data, error } = await getScopedClient()
        .from('webinars')
        .select('id, status, error_message, title, subtitle, webinar_script, marketing_emails, follow_up_emails, social_posts, landing_page, offer_stack')
        .eq('job_id', jobId)
        .single();

      if (error || !data) {
        // Record not created yet by Make.com - keep polling
        return;
      }

      const supabaseRow = data as SupabaseWebinarRow;

      if (supabaseRow.status === 'complete') {
        cleanup();
        handleCompleteSupabase(supabaseRow);
        return;
      } else if (supabaseRow.status === 'error') {
        cleanup();
        setState({
          kind: 'error',
          message:
            supabaseRow.error_message ||
            'An error occurred while generating your content. Please try again.',
          canRetry: true,
        });
        return;
      }
      // status === 'processing' — keep polling
    } catch (error) {
      // Don't stop polling on transient network errors
      console.error('Polling error:', error);
    }
  }, [jobId, cleanup, handleCompleteSupabase, locationId]);

  useEffect(() => {
    if (!jobId) {
      navigate('/', { replace: true });
      return;
    }

    startTimeRef.current = Date.now();

    // Elapsed time ticker
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setElapsedMs(elapsed);

      if (elapsed >= POLLING_CONFIG.timeoutMs) {
        cleanup();
        setState({ kind: 'timeout' });
      }
    }, 1000);

    // Stage message rotation
    stageRef.current = setInterval(() => {
      setStageIndex((prev) =>
        prev < STAGE_MESSAGES.length - 1 ? prev + 1 : prev,
      );
    }, STAGE_ROTATE_MS);

    // Poll immediately, then on interval
    poll();
    intervalRef.current = setInterval(poll, POLLING_CONFIG.intervalMs);

    return cleanup;
  }, [jobId, navigate, poll, cleanup]);

  useEffect(() => {
    if (state.kind !== 'polling') return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    [90000, 180000, 270000].forEach((delay, i) => {
      timers.push(setTimeout(() => setPhase(i + 1), delay));
    });

    return () => timers.forEach(clearTimeout);
  }, [state.kind]);

  const handleCheckAgain = () => {
    setState({ kind: 'polling' });
    setElapsedMs(0);
    startTimeRef.current = Date.now();
    setStageIndex(0);
    setPhase(0);

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= POLLING_CONFIG.timeoutMs) {
        cleanup();
        setState({ kind: 'timeout' });
      }
    }, 1000);

    stageRef.current = setInterval(() => {
      setStageIndex((prev) =>
        prev < STAGE_MESSAGES.length - 1 ? prev + 1 : prev,
      );
    }, STAGE_ROTATE_MS);

    poll();
    intervalRef.current = setInterval(poll, POLLING_CONFIG.intervalMs);
  };

  const handleStartOver = () => {
    localStorage.removeItem(STORAGE_KEYS.currentJobId);
    localStorage.removeItem(STORAGE_KEYS.webinarResults);
    navigate('/');
  };

  // --- Render ---

  if (state.kind === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg"
        >
          <Card className="text-center space-y-6 p-8">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-red-500/10 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-slate-100">
                Something Went Wrong
              </h2>
              <p className="text-slate-400">{state.message}</p>
            </div>
            <div className="flex flex-col gap-3">
              {state.canRetry && (
                <Button onClick={handleCheckAgain}>
                  <RotateCcw className="w-4 h-4" />
                  Try Again
                </Button>
              )}
              <Button variant="secondary" onClick={handleStartOver}>
                <Home className="w-4 h-4" />
                Start Over
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (state.kind === 'timeout') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg"
        >
          <Card className="text-center space-y-6 p-8">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-amber-500/10 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-amber-400" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-slate-100">
                Taking Longer Than Expected
              </h2>
              <p className="text-slate-400">
                Your content may still be processing. You can check again or
                start over.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button onClick={handleCheckAgain}>
                <RotateCcw className="w-4 h-4" />
                Check Again
              </Button>
              <Button variant="secondary" onClick={handleStartOver}>
                <Home className="w-4 h-4" />
                Start Over
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Polling / complete state
  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg"
      >
        <Card className="text-center space-y-8 p-8">
          {state.kind === 'polling' ? (
            <>
              {/* Alien Processor Animation */}
              <AlienProcessor phase={phase} />

              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-slate-100">
                  Generating Your Webinar
                </h2>
                <p className="text-sm text-slate-500">
                  {formatElapsed(elapsedMs)} elapsed
                </p>
              </div>

              {/* Subtle stage indicator */}
              <AnimatePresence mode="wait">
                <motion.p
                  key={stageIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="text-sm text-slate-400"
                >
                  {STAGE_MESSAGES[stageIndex]}
                </motion.p>
              </AnimatePresence>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-green-500/20 to-primary-500/20 flex items-center justify-center"
              >
                <CheckCircle2 className="w-10 h-10 text-green-400" />
              </motion.div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-slate-100">
                  Content Ready!
                </h2>
                <p className="text-slate-400">
                  Redirecting to your results...
                </p>
              </div>
            </motion.div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
